import { addCameraParticipantRequest, addCameraParticipantSuccess, removeCameraParticipantFailure, removeCameraParticipantRequest, removeCameraParticipantSuccess } from '../../../camera-participant/actions';
import { constructOptions } from '../../connection/actions.any';
import { JITSI_CONNECTION_URL_KEY } from '../../connection/constants';
import JitsiMeetJS from '../../lib-jitsi-meet';
import { getLocalParticipant } from '../../participants/functions';
import { getConferenceOptions, sendLocalParticipant } from '../functions';
/**
 * Obtiene el ID del dispositivo de cámara del participante local.
 *
 * @async
 * @function
 * @returns {Promise<string>} Una promesa que resuelve al ID del dispositivo de cámara del participante local.
 */
export async function getLocalMediaId() {
    const localParticipant = getLocalParticipant(APP.store.getState());
    if (!localParticipant) {
        return '';
    }
    return localParticipant.cameraDeviceId ?? '';
}
const virtualParticipantRegistry = new Map();
export function isVirtualParticipantAdded(deviceId) {
    return virtualParticipantRegistry.has(deviceId);
}
/**
 * Agrega las diferentes fuentes de video cómo participantes en la videoconferencia.
 *
 * @returns { void }
 */
export async function addCaramaAsVirtualParticipant() {
    const devices = await getVideoSources();
    const localUserVideoId = await getLocalMediaId();
    const foundedVideoDevices = devices?.filter(device => device.deviceId !== localUserVideoId) ?? [];
    if (foundedVideoDevices.length > 0) {
        for (const device of foundedVideoDevices) {
            console.log('Cantidad de dispositivos encontrados:', foundedVideoDevices);
            await addVirtualParticipantToConference(APP.store.getState(), device.deviceId);
        }
    }
}
/**
 * Agrega un participante virtual a la videoconferencia.
 *
 * @param { IReduxState } state - Estados de Redux.
 * @param { string } deviceId - ID de la cámara a agregar como participante virtual
 * ID de la cámara usada por el participante local para evitar cámaras sublicadas.
 *
 * @returns { void }
 */
export async function addVirtualParticipantToConference(state, deviceId) {
    const { options, locationURL } = getConferenceData(state);
    const { jwt } = state['features/base/jwt'];
    //@ts-ignore
    const { dispatch } = APP.store;
    dispatch(addCameraParticipantRequest(deviceId));
    const connection = new JitsiMeetJS.JitsiConnection(options.appId, jwt, options);
    connection[JITSI_CONNECTION_URL_KEY] = locationURL;
    connection.addEventListener(JitsiMeetJS.events.connection.CONNECTION_ESTABLISHED, async () => {
        const roomName = APP.conference.roomName;
        const conferenceOptions = getConferenceOptions(state);
        const virtualRoom = connection.initJitsiConference(roomName, conferenceOptions);
        const virtualParticipant = createVirtualParticipant(connection);
        virtualParticipant.cameraDeviceId = deviceId;
        configureVirtualRoomEvents(virtualRoom, virtualParticipant);
        addLocalTracksToVirtualRoom(virtualRoom, deviceId);
        sendLocalParticipant(state, virtualRoom);
        virtualRoom.setDisplayName(virtualParticipant.displayName);
        virtualRoom.join();
        // Guarda al participante virutal  para poder luego eliminarlo
        dispatch(addCameraParticipantSuccess(deviceId, virtualRoom, connection));
        virtualParticipantRegistry.set(deviceId, {
            connection,
            virtualRoom
        });
    });
    connection.connect();
}
/**
 * Elimina de la videoconferencia al participante virtual de esta cámara.
 */
export async function removeVirtualParticipantFromConference(state, deviceId) {
    //@ts-ignore
    const { dispatch } = APP.store;
    dispatch(removeCameraParticipantRequest(deviceId));
    const entry = virtualParticipantRegistry.get(deviceId);
    if (!entry) {
        dispatch(removeCameraParticipantFailure(deviceId, `No existe participante virtual para ${deviceId}`));
        throw new Error(`No existe participante virtual para ${deviceId}`);
    }
    // 1) el “usuario” sale de la conferencia
    entry.virtualRoom.leave();
    // 2) cerramos la conexión
    entry.connection.disconnect();
    dispatch(removeCameraParticipantSuccess(deviceId));
    // 3) lo borramos del registro
    virtualParticipantRegistry.delete(deviceId);
}
/**
 * Genera los datos de un participante virtual.
 *
 * @function
 * @returns {{ participantId: string, participantName: string, role: string }}
 * Un objeto con el ID, nombre y rol del participante virtual.
 */
function getVirtualParticipantData() {
    const participantId = `virtualParticipant-${Date.now()}`;
    return {
        participantId,
        participantName: `Cámara Virtual ${participantId}`,
        role: 'moderator'
    };
}
// eslint-disable-next-line valid-jsdoc
/**
 * Obtiene los datos de la conferencia basados en el estado de la aplicación.
 *
 * @function
 * @param {IReduxState} state - El estado global de la aplicación Redux.
 * @returns {{ options: IOptions, locationURL: URL | undefined }}
 * Un objeto con las opciones de la conferencia y la URL de ubicación.
 */
function getConferenceData(state) {
    const { locationURL } = state['features/base/connection'];
    return {
        options: constructOptions(state),
        locationURL
    };
}
/**
 *  Crea el usuairo virutal a agregar en la videoconferencia.
 *
 * @param { IJitsiConference } conference -
 * Objeto con la videoconferencia acual al que se le agrega el usuario virtrual.
 * @returns { IVirtualParticipant } Participante virtual a agregar a la videoconferencia.
 */
function createVirtualParticipant(conference) {
    const { participantId, participantName } = getVirtualParticipantData();
    const virtualParticipant = {
        id: participantId,
        displayName: participantName,
        role: 'moderator',
        conference,
        sources: new Map()
    };
    virtualParticipant.sources.set('video', new Map([[
            'camera', {
                muted: false,
                videoType: 'camera'
            }
        ]]));
    return virtualParticipant;
}
/**
 * Configura los eventos para la sala virtual.
 *
 * @param { virtualRoom } virtualRoom - La sala virtual a la cual se conectará.
 * @param { virtualParticipant } virtualParticipant - Participante virtual el cual se unirá.
 * @returns { void }
 */
function configureVirtualRoomEvents(virtualRoom, virtualParticipant) {
    virtualRoom.on(JitsiMeetJS.events.conference.CONFERENCE_JOINED, () => {
        console.log('Participante virtual se ha unido a la conferencia');
        // APP.store.dispatch(participantJoined(virtualParticipant));
    });
    virtualRoom.on(JitsiMeetJS.events.conference.USER_JOINED, (id) => {
        console.log(`Participante detectado con ID: ${id}`);
    });
}
/**
 * Agrega los tracks locales al participante virtual.
 *
 * @param { IJitsiConference } virtualRoom - Sala virtual a al cual agregar el track.
 * @param { string } deviceId - Cámara virtual a agregar a la sala.
 *
 * @returns { void }
 */
function addLocalTracksToVirtualRoom(virtualRoom, deviceId) {
    JitsiMeetJS.createLocalTracks({
        devices: ['video'],
        cameraDeviceId: deviceId
    })
        .then((tracks) => {
        if (tracks) {
            tracks.forEach(track => virtualRoom.addTrack(track));
        }
    })
        .catch((error) => {
        console.error('Error al crear los tracks locales para el participante virtual:', error);
    });
}
/**
 * Obtiene una lista de dispositivos de entrada de video disponibles en el sistema.
 *
 * @returns {Promise<MediaDeviceInfo[]>} - Una promesa que resuelve con la lista de dispositivos de video.
 */
export async function getVideoSources() {
    try {
        if (!navigator.mediaDevices?.enumerateDevices) {
            throw new Error('La API de dispositivos multimedia no es compatible con este navegador.');
        }
        await navigator.mediaDevices.getUserMedia({ video: true });
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoSources = devices.filter(device => device.kind === 'videoinput');
        console.log('Dispositivos de video detectados:', videoSources);
        return videoSources;
    }
    catch (error) {
        console.error('Error al obtener las fuentes de video:', error);
        throw error;
    }
}
let previouslyConnectedCameras = [];
export async function startCameraMonitoring(onNewCameraConnectedCallback) {
    try {
        previouslyConnectedCameras = await getVideoSources();
        console.log('Cámaras iniciales detectadas:', previouslyConnectedCameras.map(d => d.label));
    }
    catch (initialLoadError) {
        if (initialLoadError instanceof Error) { // Verificación del tipo
            if (initialLoadError.name === 'NotFoundError') {
                console.warn('No se encontraron cámaras al iniciar el monitoreo. Esperando conexiones futuras.');
            }
            else {
                console.error('Error inesperado al obtener cámaras iniciales para monitoreo:', initialLoadError.message);
            }
        }
        else {
            console.error('Error desconocido al obtener cámaras iniciales para monitoreo:', initialLoadError);
        }
        previouslyConnectedCameras = [];
    }
    navigator.mediaDevices.addEventListener('devicechange', async () => {
        console.log('Evento devicechange detectado.');
        let currentCameras = [];
        try {
            currentCameras = await getVideoSources();
        }
        catch (deviceChangeError) {
            if (deviceChangeError instanceof Error) { // Verificación del tipo
                if (deviceChangeError.name === 'NotFoundError') {
                    console.warn('No se encontraron cámaras durante el cambio de dispositivo. Posiblemente se desconectó la última cámara.');
                    currentCameras = [];
                }
                else {
                    console.error('Error inesperado al obtener cámaras durante devicechange:', deviceChangeError.message);
                }
            }
            else {
                console.error('Error desconocido al obtener cámaras durante devicechange:', deviceChangeError);
            }
        }
        // Identificar nuevas cámaras comparando con las cámaras previamente conectada
        if (currentCameras.length > 0) {
            const newCameras = currentCameras.filter(currentCam => !previouslyConnectedCameras.some(prevCam => prevCam.deviceId === currentCam.deviceId));
            if (newCameras.length > 0) {
                console.log('Nuevas cámaras detectadas:', newCameras.map(c => c.label));
                for (const newCam of newCameras) {
                    onNewCameraConnectedCallback(newCam.deviceId);
                }
            }
        }
        // Actualizar la lista de cámaras conectadas, incluso si está vacía
        previouslyConnectedCameras = currentCameras;
    });
}
